import * as React from 'react'
import { Input, Card, CardBody, FormGroup, Label } from 'reactstrap'
import { DropdownComponent, DropdownOption, OnChangeEvent } from 'components/dropdown/component'
import * as Modals from 'shared/modals'
import * as ImageConstants from './constants'
import { ScoutIcon } from 'components/icons/scout'
import './styles.css'

interface Props extends Modals.ModalComponentProps {
  name: string
  options: {
    style: string
    quality: string
    subjectFocus: string
    colorScheme: string
    mood: string
    complexity: string
    artStyle: string
    lighting: string
  }
  isDefault: boolean
  createOrUpdate: (payload: any) => Promise<any>
}

interface State extends Modals.ModalComponentState {
  name: string
  style: DropdownOption
  quality: DropdownOption
  subjectFocus: DropdownOption
  colorScheme: DropdownOption
  mood: DropdownOption
  complexity: DropdownOption
  artStyle: DropdownOption
  lighting: DropdownOption
  isDefault: boolean
  error: string
}

const emptyState: State = {
  name: '',
  style: ImageConstants.ImageStyleOptions[0], // Realistic Photography
  quality: ImageConstants.ImageQualityOptions[1], // Standard
  subjectFocus: ImageConstants.SubjectFocusOptions[5], // Business
  colorScheme: ImageConstants.ColorSchemeOptions[7], // Natural
  mood: ImageConstants.MoodOptions[0], // Professional
  complexity: ImageConstants.ComplexityOptions[1], // Moderate
  artStyle: ImageConstants.ArtStyleOptions[0], // Modern
  lighting: ImageConstants.LightingOptions[0], // Natural
  isDefault: false,
  error: '',
}

export class AddAIImagePreferenceModal extends Modals.GenericModalComponent<Props, State> {
  public static key: string = 'AddAIImagePreferenceModal'

  public static defaultProps = {
    closeButtonText: 'Cancel',
    saveText: 'Save Preference',
    secondAction: Modals.ModalActionType.BUTTON,
  }

  constructor(props: Props) {
    super(props, AddAIImagePreferenceModal.key)
    this.state = emptyState
  }

  public componentDidMount() {
    this.loadFormData()
  }

  public componentDidUpdate(prevProps: Props) {
    // If props changed (modal opened with new data), reload form
    if (prevProps.name !== this.props.name || prevProps.options !== this.props.options) {
      this.loadFormData()
    }
  }

  private loadFormData = () => {
    // If editing existing preference, populate form
    if (this.props.name) {
      const styleOption = ImageConstants.ImageStyleOptions.find(s => s.value === (this.props.options && this.props.options.style)) || ImageConstants.ImageStyleOptions[0]
      const qualityOption = ImageConstants.ImageQualityOptions.find(q => q.value === (this.props.options && this.props.options.quality)) || ImageConstants.ImageQualityOptions[1]
      const subjectFocusOption = ImageConstants.SubjectFocusOptions.find(a => a.value === (this.props.options && this.props.options.subjectFocus)) || ImageConstants.SubjectFocusOptions[5]
      const colorSchemeOption = ImageConstants.ColorSchemeOptions.find(c => c.value === (this.props.options && this.props.options.colorScheme)) || ImageConstants.ColorSchemeOptions[7]
      const moodOption = ImageConstants.MoodOptions.find(m => m.value === (this.props.options && this.props.options.mood)) || ImageConstants.MoodOptions[0]
      const complexityOption = ImageConstants.ComplexityOptions.find(c => c.value === (this.props.options && this.props.options.complexity)) || ImageConstants.ComplexityOptions[1]
      const artStyleOption = ImageConstants.ArtStyleOptions.find(a => a.value === (this.props.options && this.props.options.artStyle)) || ImageConstants.ArtStyleOptions[0]
      const lightingOption = ImageConstants.LightingOptions.find(l => l.value === (this.props.options && this.props.options.lighting)) || ImageConstants.LightingOptions[0]

      this.setState({
        name: this.props.name,
        style: styleOption,
        quality: qualityOption,
        subjectFocus: subjectFocusOption,
        colorScheme: colorSchemeOption,
        mood: moodOption,
        complexity: complexityOption,
        artStyle: artStyleOption,
        lighting: lightingOption,
        isDefault: this.props.isDefault || false,
      })
    } else {
      // Reset to empty state for new preference
      this.setState(emptyState)
    }
  }

  protected getTitleText(data: any): string {
    return this.props.name ? 'Edit AI Image Preference' : 'Add New AI Image Preference'
  }

  protected xButtonClick(data: any) {
    this.clearState()
    super.xButtonClick(data)
  }

  protected close(data: any) {
    this.clearState()
    super.close(data)
  }

  protected save(data: any) {
    if (!this.state.name.trim()) {
      this.setState({ error: 'Please enter a preference name.' })
      return
    }

    const payload = {
      name: this.state.name.trim(),
      options: {
        style: this.state.style.value,
        quality: this.state.quality.value,
        subjectFocus: this.state.subjectFocus.value,
        colorScheme: this.state.colorScheme.value,
        mood: this.state.mood.value,
        complexity: this.state.complexity.value,
        artStyle: this.state.artStyle.value,
        lighting: this.state.lighting.value,
      },
      is_default: this.state.isDefault,
    }

    this.props.createOrUpdate(payload)
      .then(() => {
        this.clearState()
        super.close(data)
      })
      .catch((error) => {
        this.setState({ error: 'Failed to save preference. Please try again.' })
      })
  }

  protected saveDisabled(data: any): boolean {
    return !this.state.name.trim()
  }

  private clearState = () => {
    this.setState(emptyState)
  }

  private setName = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ name: event.target.value, error: '' })
  }

  private setStyle = (event: OnChangeEvent) => {
    this.setState({ style: event.selected })
  }

  private setQuality = (event: OnChangeEvent) => {
    this.setState({ quality: event.selected })
  }

  private setSubjectFocus = (event: OnChangeEvent) => {
    this.setState({ subjectFocus: event.selected })
  }

  private setColorScheme = (event: OnChangeEvent) => {
    this.setState({ colorScheme: event.selected })
  }

  private setMood = (event: OnChangeEvent) => {
    this.setState({ mood: event.selected })
  }

  private setComplexity = (event: OnChangeEvent) => {
    this.setState({ complexity: event.selected })
  }

  private setArtStyle = (event: OnChangeEvent) => {
    this.setState({ artStyle: event.selected })
  }

  private setLighting = (event: OnChangeEvent) => {
    this.setState({ lighting: event.selected })
  }

  private setIsDefault = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ isDefault: event.target.checked })
  }

  private generatePreviewText = (): string => {
    return `${this.state.style.description} image in ${this.state.artStyle.description.toLowerCase()} style with ${this.state.mood.description.toLowerCase()} mood, ${this.state.colorScheme.description.toLowerCase()}, ${this.state.lighting.description.toLowerCase()}, and ${this.state.complexity.description.toLowerCase()}.`
  }

  protected renderChildren(data: any) {
    return (
      <div className="ai-image-preference-modal">
        <div className="ai-image-preference-form">
          {this.state.error && (
            <div className="alert alert-danger" role="alert">
              {this.state.error}
            </div>
          )}

          <FormGroup>
            <Label className="ai-image-preference-label">Preference Name</Label>
            <Input
              type="text"
              value={this.state.name}
              onChange={this.setName}
              placeholder="Enter a name for this preference"
              className="ai-image-preference-input"
            />
          </FormGroup>

          <Card className="ai-image-preference-settings-card">
            <CardBody>
              <div className="ai-image-options-header mb-3 text-center">
                <div className="d-flex align-items-center justify-content-center">
                  <ScoutIcon svgwidth="24" svgheight="24" className="mr-2" />
                  <h6 className="ai-image-preference-settings-title mb-0">AI Image Generation Settings</h6>
                </div>
                <p className="text-muted mb-0 mt-1">Configure how AI generates images for this preference</p>
              </div>

              <div className="ai-image-preference-settings-grid">
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Image Style</Label>
                      <DropdownComponent
                        data={ImageConstants.ImageStyleOptions}
                        selected={this.state.style.key}
                        onChange={this.setStyle}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Image Quality</Label>
                      <DropdownComponent
                        data={ImageConstants.ImageQualityOptions}
                        selected={this.state.quality.key}
                        onChange={this.setQuality}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Subject Focus</Label>
                      <DropdownComponent
                        data={ImageConstants.SubjectFocusOptions}
                        selected={this.state.subjectFocus.key}
                        onChange={this.setSubjectFocus}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Color Scheme</Label>
                      <DropdownComponent
                        data={ImageConstants.ColorSchemeOptions}
                        selected={this.state.colorScheme.key}
                        onChange={this.setColorScheme}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Mood</Label>
                      <DropdownComponent
                        data={ImageConstants.MoodOptions}
                        selected={this.state.mood.key}
                        onChange={this.setMood}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Complexity</Label>
                      <DropdownComponent
                        data={ImageConstants.ComplexityOptions}
                        selected={this.state.complexity.key}
                        onChange={this.setComplexity}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Art Style</Label>
                      <DropdownComponent
                        data={ImageConstants.ArtStyleOptions}
                        selected={this.state.artStyle.key}
                        onChange={this.setArtStyle}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Lighting</Label>
                      <DropdownComponent
                        data={ImageConstants.LightingOptions}
                        selected={this.state.lighting.key}
                        onChange={this.setLighting}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>
                </div>
              </div>

              <div className="ai-image-preview-info mt-3">
                <div className="alert alert-info">
                  <strong>Preview:</strong> {this.generatePreviewText()}
                </div>
              </div>
            </CardBody>
          </Card>

          <FormGroup className="ai-image-preference-default-toggle">
            <div className="form-check mb-2">
              <input
                type="checkbox"
                id="isDefaultImageSwitch"
                checked={this.state.isDefault}
                onChange={this.setIsDefault}
                className="form-check-input"
                style={{ 
                  width: '18px', 
                  height: '18px', 
                  cursor: 'pointer'
                } as React.CSSProperties}
              />
              <label htmlFor="isDefaultImageSwitch" className="form-check-label" style={{ cursor: 'pointer', fontWeight: 500, marginLeft: '8px' }}>
                Set as default preference
              </label>
            </div>
            <small className="form-text text-muted" style={{ marginLeft: '26px' }}>
              The default preference will be automatically selected when generating AI images.
            </small>
          </FormGroup>
        </div>
      </div>
    )
  }
}